/**
 * chartConfig.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for all MarketPulze chart configuration.
 *
 * Exports:
 *   CHART_MARGIN          — SVG margin object {top, right, bottom, left}
 *   PANEL_MAX_MEMBER      — max panel indicators for logged-in members
 *   PANEL_MAX_GUEST       — max panel indicators for guests
 *   OVERLAY_MAX_GUEST     — max overlay indicators for guests
 *   TABS                  — timeframe tab definitions
 *   CHART_TYPES           — available chart type definitions
 *   MODE_CONFIG           — per-mode feature flags (height removed — now flex:1)
 *   IND_COLORS            — per-indicator colour palettes
 *   DRAWING_COLORS        — per-drawing-tool colours
 *   FIB_LEVELS            — 10 Fibonacci retracement + extension levels
 *   TEMPLATES             — pre-built indicator bundles
 *   INDICATOR_REGISTRY    — master indicator definition map
 *   getNextColor()        — colour picker for new indicator instances
 *   API_URL               — backend API base URL from env
 *   WS_TIINGO_KEY         — Tiingo WebSocket API key from env
 *   WS_FINNHUB_KEY        — Finnhub WebSocket API key from env
 *   WS_PROVIDER           — resolved active WebSocket provider
 *
 * FIX (v8): Added PANEL_MAX_MEMBER, PANEL_MAX_GUEST, OVERLAY_MAX_GUEST —
 *   these existed in the monolith (lines 46-48) but were never extracted.
 *   Without them the lean PriceChart.jsx cannot enforce indicator limits.
 *
 * FIX (v8): Reverted template key "oversoldob" → "oversold" and restored
 *   original indicator set (BB + Stochastic). The rename broke all bookmarks
 *   and shared links that used ?template=oversold. The indicator set change
 *   was not an intentional product decision.
 *
 * Why everything lives here:
 *   Adding a new indicator = add one entry to INDICATOR_REGISTRY.
 *   Adding a new timeframe = add one entry to TABS.
 *   Adding a new chart type = add one entry to CHART_TYPES.
 *   Nothing else in the codebase needs to change.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  calcSMA, calcEMA, calcBollinger, calcVWAPBands,
  calcMACD, calcRSI, calcStochastic, calcATR,
  calcOBV, calcCCI, calcWilliamsR, calcMFI, calcROC,
  calcPSAR, calcADX, calcIchimoku, calcDonchian,
  calcVolumeProfile,
} from '../lib/calcIndicators';

// ── Environment / API Config ──────────────────────────────────────────────────

export const API_URL        = process.env.REACT_APP_API_URL    || 'http://localhost:5000';
export const WS_TIINGO_KEY  = process.env.REACT_APP_TIINGO_KEY  || '';
export const WS_FINNHUB_KEY = process.env.REACT_APP_FINNHUB_KEY || '';
export const WS_PROVIDER    = WS_TIINGO_KEY ? 'tiingo' : WS_FINNHUB_KEY ? 'finnhub' : null;

// ── Indicator Limits ──────────────────────────────────────────────────────────

/**
 * PANEL_MAX_MEMBER — max panel (sub-chart) indicators for logged-in members
 * PANEL_MAX_GUEST  — max panel indicators for unauthenticated guests
 * OVERLAY_MAX_GUEST — max overlay indicators for unauthenticated guests
 *
 * Tier gating is done in PriceChart.jsx addIndicator handler:
 *   const panelMax = isLoggedIn ? PANEL_MAX_MEMBER : PANEL_MAX_GUEST;
 */
export const PANEL_MAX_MEMBER  = 8;
export const PANEL_MAX_GUEST   = 2;
export const OVERLAY_MAX_GUEST = 3;

// ── Chart Layout ──────────────────────────────────────────────────────────────

/**
 * CHART_MARGIN
 * SVG inner margin in pixels.
 * top: 20px    — breathing room above candles (was 8px — candles touched top edge)
 * right: 68px  — y-axis labels + indicator price pins + alert zone
 * bottom: 36px — x-axis date labels + volume bar breathing room (was 28px)
 * left: 4px    — minimal — drawing toolbar sits left of SVG container
 *
 * FIX (v8 HOTFIX-6): top 8→20, bottom 28→36 for visual breathing room.
 */
export const CHART_MARGIN = { top: 20, right: 68, bottom: 36, left: 4 };

// ── Timeframe Tabs ────────────────────────────────────────────────────────────

/**
 * TABS — timeframe tab definitions.
 * 1D uses days:2 (not 1) to ensure the fetch always covers a full session
 * regardless of what time the request fires (S27FIX2).
 */
export const TABS = [
  { label: '1D', resolution: '5',  days: 2    },
  { label: '1W', resolution: '60', days: 7    },
  { label: '1M', resolution: 'D',  days: 30   },
  { label: '3M', resolution: 'D',  days: 90   },
  { label: '1Y', resolution: 'D',  days: 365  },
  { label: '5Y', resolution: 'W',  days: 1825 },
];

// ── Chart Types ───────────────────────────────────────────────────────────────

export const CHART_TYPES = [
  { key: 'candlestick', label: 'Candlestick', shortLabel: 'C'  },
  { key: 'heikinashi',  label: 'Heikin Ashi', shortLabel: 'HA' },
  { key: 'ohlc',        label: 'OHLC Bar',    shortLabel: 'O'  },
  { key: 'mountain',    label: 'Area',         shortLabel: 'A'  },
  { key: 'line',        label: 'Line',         shortLabel: 'L'  },
  { key: 'renko',       label: 'Renko',        shortLabel: 'R'  },
];

// ── Mode Configuration ────────────────────────────────────────────────────────

/**
 * MODE_CONFIG
 * Controls which features are shown per context.
 * height is removed — all modes use flex:1 via the ResizeObserver approach
 * so the SVG always fills its actual container precisely.
 *
 * sidebar: show right sidebar (active indicators + tabs)
 * panels:  show RSI/MACD sub-panels below chart
 * pills:   show timeframe tab pills in toolbar
 */
export const MODE_CONFIG = {
  embedded:   { sidebar: false, panels: false, pills: true  },
  fullscreen: { sidebar: true,  panels: true,  pills: true  },
  floating:   { sidebar: false, panels: false, pills: false },
  tab:        { sidebar: true,  panels: true,  pills: true  },
};

// ── Colour Palettes ───────────────────────────────────────────────────────────

export const IND_COLORS = {
  sma:          ['#f59e0b','#3b82f6','#10b981','#ef4444','#a855f7','#06b6d4'],
  ema:          ['#f97316','#ec4899','#facc15','#fb923c','#f43f5e','#c084fc'],
  bb:           ['#6366f1','#818cf8','#4f46e5'],
  vwap:         ['#06b6d4','#22d3ee'],
  ichimoku:     ['#00d4ff'],
  psar:         ['#a855f7','#d946ef'],
  donchian:     ['#0ea5e9','#38bdf8'],
  rsi:          ['#7b2fff','#8b5cf6'],
  macd:         ['#a855f7','#7c3aed'],
  stoch:        ['#10b981','#34d399'],
  adx:          ['#00d4ff','#38bdf8'],
  atrindicator: ['#f59e0b','#fbbf24'],
  obv:          ['#10b981','#6ee7b7'],
  cci:          ['#ec4899','#f472b6'],
  williamsR:    ['#00d4ff','#60c4fb'],
  mfi:          ['#f97316','#fb923c'],
  roc:          ['#a855f7','#c084fc'],
};

/**
 * getNextColor(key, activeIndicators)
 * Returns the next available colour for a new indicator instance.
 * Cycles through IND_COLORS[key] based on how many instances already exist.
 */
export function getNextColor(key, activeIndicators) {
  const palette  = IND_COLORS[key] || ['#8b8ba7'];
  const existing = activeIndicators.filter(i => i.key === key).length;
  return palette[existing % palette.length];
}

// ── Drawing Colours ───────────────────────────────────────────────────────────

export const DRAWING_COLORS = {
  horizontal:    '#6366f1',
  trendline:     '#f59e0b',
  fibonacci:     '#f59e0b',
  rectangle:     '#6366f1',
  anchored_vwap: '#06b6d4',
  ray:           '#6366f1',
  measure:       '#00d4ff',
};

// ── Fibonacci Levels ──────────────────────────────────────────────────────────

export const FIB_LEVELS = [
  { ratio: 0,     label: '0%',     color: 'rgba(99,102,241,0.7)',  strokeW: 1   },
  { ratio: 0.236, label: '23.6%',  color: 'rgba(99,102,241,0.5)',  strokeW: 1   },
  { ratio: 0.382, label: '38.2%',  color: '#10b981',               strokeW: 1   },
  { ratio: 0.5,   label: '50%',    color: 'rgba(226,232,240,0.4)', strokeW: 1   },
  { ratio: 0.618, label: '61.8%',  color: '#f59e0b',               strokeW: 1.5 },
  { ratio: 0.786, label: '78.6%',  color: 'rgba(99,102,241,0.5)',  strokeW: 1   },
  { ratio: 1.0,   label: '100%',   color: 'rgba(99,102,241,0.7)',  strokeW: 1   },
  { ratio: 1.272, label: '127.2%', color: 'rgba(239,68,68,0.45)',  strokeW: 1   },
  { ratio: 1.618, label: '161.8%', color: 'rgba(239,68,68,0.6)',   strokeW: 1   },
  { ratio: 2.0,   label: '200%',   color: 'rgba(239,68,68,0.35)',  strokeW: 1   },
];

// ── Templates ─────────────────────────────────────────────────────────────────

/**
 * TEMPLATES — pre-built indicator combinations.
 * Template keys match URL param values (?template=daytrader).
 *
 * FIX (v8): Reverted key "oversoldob" → "oversold" (breaks ?template=oversold
 * bookmarks/shared links). Restored indicator set: BB(20,2) + Stoch(14,3)
 * which is the original and most useful combination for oversold/OB detection.
 */
export const TEMPLATES = {
  daytrader: {
    label: 'Day Trader', color: '#00d4ff',
    indicators: [
      { key: 'vwap', params: {} },
      { key: 'ema',  params: { period: 9  } },
      { key: 'ema',  params: { period: 21 } },
    ],
  },
  swingtrader: {
    label: 'Swing Trader', color: '#a855f7',
    indicators: [
      { key: 'sma',  params: { period: 20 } },
      { key: 'sma',  params: { period: 50 } },
      { key: 'macd', params: { fast: 12, slow: 26, signal: 9 } },
    ],
  },
  breakout: {
    label: 'Breakout Hunter', color: '#f59e0b',
    indicators: [
      { key: 'bb',   params: { period: 20, mult: 2 } },
      { key: 'vwap', params: {} },
    ],
  },
  oversold: {
    // KEY IS "oversold" — do NOT rename. ?template=oversold must keep working.
    label: 'Oversold/OB', color: '#10b981',
    indicators: [
      { key: 'bb',    params: { period: 20, mult: 2 } },
      { key: 'stoch', params: { k: 14, d: 3 } },
    ],
  },
  trendfollower: {
    label: 'Trend Follower', color: '#ef4444',
    indicators: [
      { key: 'ema', params: { period: 50  } },
      { key: 'ema', params: { period: 200 } },
      { key: 'adx', params: { period: 14  } },
    ],
  },
};

// ── Indicator Registry ────────────────────────────────────────────────────────

/**
 * INDICATOR_REGISTRY — master definition map for all 17 supported indicators.
 *
 * Each entry defines:
 *   label, desc, type, defaultParams, paramDefs, refLines, range, note, calc, d3Signal
 *
 * calc() result formats:
 *   {lines: [{data:[{time,value}], color, dash?}]}   — overlays + panels
 *   {macd: {macdLine, signalLine, histogram}}         — MACD panel
 *   {points: [{time, value, bull, color}]}            — PSAR
 *   {cloud: {tenkan, kijun, senkouA, senkouB}}        — Ichimoku
 *   {volumeProfile: {levels, pocPrice, maxVol}}       — Volume Profile
 *
 * To add a new indicator:
 *   1. Add calc function to calcIndicators.js and export it
 *   2. Import it here
 *   3. Add entry to INDICATOR_REGISTRY
 *   → Appears automatically in drawer, tooltip, y-axis pins, D3 signals
 */
export const INDICATOR_REGISTRY = {
  sma: {
    label: 'SMA', type: 'overlay',
    desc: 'Simple Moving Average — smooths price over N bars to show trend direction.',
    defaultParams: { period: 20 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 500, default: 20, step: 1 }],
    calc: (c, p, _, color) => ({ lines: [{ data: calcSMA(c, p.period || 20), color }] }),
    d3Signal: 'sma_period',
  },
  ema: {
    label: 'EMA', type: 'overlay',
    desc: 'Exponential Moving Average — weights recent prices more heavily, reacts faster.',
    defaultParams: { period: 9 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 500, default: 9, step: 1 }],
    calc: (c, p, _, color) => ({ lines: [{ data: calcEMA(c, p.period || 9), color }] }),
    d3Signal: 'ema_period',
  },
  bb: {
    label: 'Bollinger Bands', type: 'overlay',
    desc: 'Three lines: middle SMA with upper/lower bands at N standard deviations. Wide = high volatility.',
    defaultParams: { period: 20, mult: 2 },
    paramDefs: [
      { name: 'period', label: 'Period', min: 2, max: 200, default: 20, step: 1  },
      { name: 'mult',   label: 'StdDev', min: 0.5, max: 4, default: 2,  step: 0.1 },
    ],
    calc: (c, p, _, color) => {
      const bb = calcBollinger(c, p.period || 20, p.mult || 2);
      return { lines: [
        { data: bb.upper,  color, dash: [4, 3] },
        { data: bb.middle, color: color + '55' },
        { data: bb.lower,  color, dash: [4, 3] },
      ]};
    },
    d3Signal: 'bb_params',
  },
  vwap: {
    label: 'VWAP', type: 'overlay', note: '1D only',
    desc: 'Volume Weighted Average Price with ±1σ/±2σ bands. Institutional benchmark.',
    defaultParams: {},
    paramDefs: [],
    calc: (c, p, isDay, color) => {
      if (!isDay) return { lines: [] };
      const b = calcVWAPBands(c);
      return { lines: [
        { data: b.vwap,   color },
        { data: b.upper1, color: color + '55', dash: [3, 2] },
        { data: b.lower1, color: color + '55', dash: [3, 2] },
        { data: b.upper2, color: color + '33', dash: [2, 3] },
        { data: b.lower2, color: color + '33', dash: [2, 3] },
      ]};
    },
    d3Signal: 'vwap_active',
  },
  ichimoku: {
    label: 'Ichimoku', type: 'overlay',
    desc: '5 lines showing trend, momentum, and support/resistance all at once. Cloud = key zone.',
    defaultParams: { tenkan: 9, kijun: 26, senkou: 52 },
    paramDefs: [
      { name: 'tenkan', label: 'Tenkan', min: 1, max: 100, default: 9,  step: 1 },
      { name: 'kijun',  label: 'Kijun',  min: 1, max: 200, default: 26, step: 1 },
      { name: 'senkou', label: 'Senkou', min: 1, max: 300, default: 52, step: 1 },
    ],
    calc: (c, p, _, color) => ({ cloud: calcIchimoku(c, p) }),
    d3Signal: 'ichimoku_params',
  },
  psar: {
    label: 'Parabolic SAR', type: 'overlay',
    desc: 'Dots above/below price showing trend direction and reversal points.',
    defaultParams: { step: 0.02, max: 0.2 },
    paramDefs: [
      { name: 'step', label: 'Step', min: 0.001, max: 0.1,  default: 0.02, step: 0.001 },
      { name: 'max',  label: 'Max',  min: 0.1,   max: 0.5,  default: 0.2,  step: 0.01  },
    ],
    calc: (c, p, _, color) => ({
      points: calcPSAR(c, p.step || 0.02, p.max || 0.2).map(d => ({ ...d, color })),
    }),
    d3Signal: 'psar_params',
  },
  donchian: {
    label: 'Donchian', type: 'overlay',
    desc: 'Highest high and lowest low over N bars. Breakout = potential new trend.',
    defaultParams: { period: 20 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 200, default: 20, step: 1 }],
    calc: (c, p, _, color) => {
      const dc = calcDonchian(c, p.period || 20);
      return { lines: [
        { data: dc.upper,  color, dash: [4, 3] },
        { data: dc.middle, color: color + '55' },
        { data: dc.lower,  color, dash: [4, 3] },
      ]};
    },
    d3Signal: 'donchian_period',
  },
  rsi: {
    label: 'RSI', type: 'panel',
    desc: 'Relative Strength Index (0–100). >70 = overbought, <30 = oversold.',
    defaultParams: { period: 14 },
    paramDefs: [{ name: 'period', label: 'Period', min: 2, max: 200, default: 14, step: 1 }],
    refLines: [
      { value: 70, color: 'rgba(239,68,68,0.6)',   label: 'OB' },
      { value: 30, color: 'rgba(16,185,129,0.6)',  label: 'OS' },
      { value: 50, color: 'rgba(139,139,167,0.2)' },
    ],
    range: [0, 100],
    calc: (c, p, _, color) => ({ lines: [{ data: calcRSI(c, p.period || 14), color }] }),
    d3Signal: 'rsi_period',
  },
  macd: {
    label: 'MACD', type: 'panel',
    desc: 'Moving Average Convergence Divergence. Histogram above zero = bullish momentum.',
    defaultParams: { fast: 12, slow: 26, signal: 9 },
    paramDefs: [
      { name: 'fast',   label: 'Fast',   min: 1, max: 100, default: 12, step: 1 },
      { name: 'slow',   label: 'Slow',   min: 1, max: 200, default: 26, step: 1 },
      { name: 'signal', label: 'Signal', min: 1, max: 50,  default: 9,  step: 1 },
    ],
    refLines: [{ value: 0, color: 'rgba(139,139,167,0.2)' }],
    calc: (c, p, _, color) => ({ macd: calcMACD(c, p.fast, p.slow, p.signal), color }),
    d3Signal: 'macd_params',
  },
  stoch: {
    label: 'Stochastic', type: 'panel',
    desc: 'Compares close to range over N bars. >80 = overbought, <20 = oversold.',
    defaultParams: { k: 14, d: 3 },
    paramDefs: [
      { name: 'k', label: '%K', min: 1, max: 100, default: 14, step: 1 },
      { name: 'd', label: '%D', min: 1, max: 50,  default: 3,  step: 1 },
    ],
    refLines: [
      { value: 80, color: 'rgba(239,68,68,0.6)',  label: 'OB' },
      { value: 20, color: 'rgba(16,185,129,0.6)', label: 'OS' },
    ],
    range: [0, 100],
    calc: (c, p, _, color) => {
      const s = calcStochastic(c, p.k || 14, p.d || 3);
      return { lines: [
        { data: s.kLine, color },
        { data: s.dLine, color: color + 'aa' },
      ]};
    },
    d3Signal: 'stoch_params',
  },
  adx: {
    label: 'ADX', type: 'panel',
    desc: 'Average Directional Index — measures trend strength. >25 = strong trend.',
    defaultParams: { period: 14 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 200, default: 14, step: 1 }],
    refLines: [{ value: 25, color: 'rgba(255,255,255,0.2)', label: 'Trend' }],
    calc: (c, p, _, color) => ({ adx: calcADX(c, p.period || 14), color }),
    d3Signal: 'adx_period',
  },
  atrindicator: {
    label: 'ATR', type: 'panel',
    desc: 'Average True Range — measures volatility. High ATR = wide swings.',
    defaultParams: { period: 14 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 200, default: 14, step: 1 }],
    calc: (c, p, _, color) => ({ lines: [{ data: calcATR(c, p.period || 14), color }] }),
    d3Signal: 'atr_active',
  },
  obv: {
    label: 'OBV', type: 'panel',
    desc: 'On-Balance Volume — cumulative volume. Rising OBV with flat price = accumulation.',
    defaultParams: {},
    paramDefs: [],
    calc: (c, p, _, color) => ({ lines: [{ data: calcOBV(c), color }] }),
    d3Signal: 'obv_active',
  },
  cci: {
    label: 'CCI', type: 'panel',
    desc: 'Commodity Channel Index. >+100 = overbought, <-100 = oversold.',
    defaultParams: { period: 20 },
    paramDefs: [{ name: 'period', label: 'Period', min: 2, max: 200, default: 20, step: 1 }],
    refLines: [
      { value:  100, color: 'rgba(239,68,68,0.5)',  label: 'OB' },
      { value: -100, color: 'rgba(16,185,129,0.5)', label: 'OS' },
    ],
    calc: (c, p, _, color) => ({ lines: [{ data: calcCCI(c, p.period || 20), color }] }),
    d3Signal: 'cci_period',
  },
  williamsR: {
    label: 'Williams %R', type: 'panel',
    desc: 'Momentum oscillator (0 to -100). Near 0 = overbought, near -100 = oversold.',
    defaultParams: { period: 14 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 200, default: 14, step: 1 }],
    refLines: [
      { value: -20, color: 'rgba(239,68,68,0.5)',  label: 'OB' },
      { value: -80, color: 'rgba(16,185,129,0.5)', label: 'OS' },
    ],
    range: [-100, 0],
    calc: (c, p, _, color) => ({ lines: [{ data: calcWilliamsR(c, p.period || 14), color }] }),
    d3Signal: 'williams_r_period',
  },
  mfi: {
    label: 'MFI', type: 'panel',
    desc: 'Money Flow Index — RSI weighted by volume. >80 = overbought, <20 = oversold.',
    defaultParams: { period: 14 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 200, default: 14, step: 1 }],
    refLines: [
      { value: 80, color: 'rgba(239,68,68,0.5)',  label: 'OB' },
      { value: 20, color: 'rgba(16,185,129,0.5)', label: 'OS' },
    ],
    range: [0, 100],
    calc: (c, p, _, color) => ({ lines: [{ data: calcMFI(c, p.period || 14), color }] }),
    d3Signal: 'mfi_period',
  },
  roc: {
    label: 'ROC', type: 'panel',
    desc: 'Rate of Change — % price change over N bars. Zero line cross = trend change.',
    defaultParams: { period: 12 },
    paramDefs: [{ name: 'period', label: 'Period', min: 1, max: 200, default: 12, step: 1 }],
    refLines: [{ value: 0, color: 'rgba(139,139,167,0.25)' }],
    calc: (c, p, _, color) => ({ lines: [{ data: calcROC(c, p.period || 12), color }] }),
    d3Signal: 'roc_period',
  },
  vprofile: {
    label: 'Vol Profile', type: 'overlay', note: 'Visible range',
    desc: 'Volume by price level. Tall bars = key zones. POC = highest-volume price.',
    defaultParams: { buckets: 24 },
    paramDefs: [{ name: 'buckets', label: 'Buckets', min: 8, max: 60, default: 24, step: 4 }],
    calc: (c, p) => ({ volumeProfile: calcVolumeProfile(c, p.buckets || 24) }),
    d3Signal: 'vprofile_poc',
  },
};
